// ContentEntry.js — Updated with disabled prop fix
import React, { useEffect, useState, useCallback } from "react";
import ReusableBreadcrumbs from "../../CommonComponents/ReusableBreadCrumbs";
import { useParams, useSearchParams } from "react-router-dom";
import InputDropdown from "../../CommonComponents/InputDropdown";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useAuth from "../../../Hooks/useAuth";
import InputField from "../../CommonComponents/InputField";
import Radio from "../../CommonComponents/Radio";
import Button from "../../Button";
import Editor from "../../Editor/ContentEditor";
import { libTypeIds, RESULT_STATUS, Roles } from "../../../constants";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
import Loading from "../../Notification-Loading/Loading/Loading";
import { getDataFromStorage } from "../../../util";

// ==================== PURE UTILITY FUNCTIONS ====================

/**
 * Calculates estimated reading time in minutes from EditorJS blocks.
 * Strips HTML tags before counting words.
 */
const calculateReadingTime = (descriptionBlocks) => {
  if (!Array.isArray(descriptionBlocks)) return 0;

  // Helper to strip HTML tags and count words
  const countWords = (htmlString) => {
    const plainText = htmlString.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");
    return plainText.trim().split(/\s+/).filter(Boolean).length;
  };

  let totalWords = 0;
  const allowedTypes = ["text", "header", "paragraph"];

  for (let block of descriptionBlocks) {
    if (allowedTypes.includes(block.type) && block.data?.text) {
      totalWords += countWords(block.data.text);
    } else if (block.type === "list" && Array.isArray(block.data?.items)) {
      for (let item of block.data.items) {
        totalWords += countWords(item.content || "");
      }
    }
  }

  return totalWords / 200; // minutes
};

/**
 * Validates video file name for allowed characters.
 * Returns true if valid, false if invalid characters found.
 */
const validateVideoFileName = (fileName) => {
  if (!fileName) return true;
  // Remove path and extension
  const baseName = fileName.split("/").pop().replace(/\.[^/.]+$/, "");
  // Allowed: alphanumeric, space, dot, hyphen, underscore
  const isValid = /^[a-zA-Z0-9.\-_ ]*$/.test(baseName);
  return isValid;
};

// ==================== COMPONENT ====================

const validators = {
  topicid: (v) => (!v ? "Topic is required" : ""),
  name: (v) => (!v ? "Name is required" : ""),
  // maxtime validation kept but commented out as per original
};

const ContentEntry = () => {
  const { type, libid } = useParams();
  const axios = useAxiosPrivate();
  const { setIsLoaded, isLoaded, navigate, setShowNotify } = useAuth();
  const [searchParams] = useSearchParams();

  const selectable = searchParams.get("selectable") === "true";
  const lpid = searchParams.get("lpId") || null;
  const moduleId = searchParams.get("moduleId") || null;
  const chapterId = searchParams.get("chapterId") || null;
  const batchId = searchParams.get("bId") || null;

  let queryString = `${
    selectable
      ? `?selectable=${selectable}${lpid ? `&lpId=${lpid}` : ""}${
          moduleId ? `&moduleId=${moduleId}` : ""
        }${chapterId ? `&chapterId=${chapterId}` : ""}${
          batchId ? `&bId=${batchId}` : ""
        }`
      : ""
  }`;

  const baseRoute = batchId ? `/batches/${batchId}/courses` : "/learningpath";
  const { onMultiPartUpload } = useS3FileManagement();

  const isEdit = Number.isInteger(+libid);

  const isAllowedToUpdate = [
    Roles.admin,
    Roles.creator,
    Roles.super_admin,
    Roles.trainer,
  ].includes(getDataFromStorage("role"));

  const items = [
    { label: "Library" },
    { label: "Contents", href:
      batchId
          ? `/library/${type}/testlist${queryString}`
          : `/library/${type}/questionlist` },
    { label: isEdit ? "Edit Content" : "Add Content" },
  ];

  const [topicOptions, setTopicOptions] = useState([]);
  const [contentDetails, setContentDetails] = useState({
    contentType: 0,
    name: "",
    topicid: null,
    maxtime: "",
  });
  const [errors, setErrors] = useState({});
  const [isProceed, setIsProceed] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  // ==================== HELPER HOOKS (useCallback) ====================

  const notify = useCallback(
    (title, msg, isUnAuth = false) => {
      setShowNotify({
        show: true,
        title,
        msg,
        isUnAuth,
      });
    },
    [setShowNotify]
  );

  const handleError = useCallback(
    (error, customMessage) => {
      const isUnauthorized = error?.message?.includes("403");
      notify(
        isUnauthorized ? "Warning !" : "Error !",
        isUnauthorized
          ? "You have been logged-out due to inactivity. Login again."
          : customMessage || error?.message || "Something went wrong",
        isUnauthorized
      );
    },
    [notify]
  );

  // ==================== TIME CALCULATION ====================

  const resolveTimeToPass = useCallback((descBlocks, maxtime, contentType) => {
    const readingTime = calculateReadingTime(descBlocks);
    let timeToPass;

    if (contentType === 1 && descBlocks.time != null) {
      // Video content: use actual video duration from description.time
      const videoMinutes = Math.round(descBlocks.time / 60);
      timeToPass = maxtime && maxtime > videoMinutes ? maxtime : videoMinutes;
    } else {
      // Content editor: use reading time
      timeToPass = Math.round(readingTime);
      timeToPass = maxtime && maxtime > timeToPass ? maxtime : timeToPass;
    }

    return timeToPass === 0 ? 1 : timeToPass;
  }, []);

  // ==================== EFFECTS ====================

  useEffect(() => {
    let isMounted = true;

    const getTopicsAndContent = async () => {
      try {
        setIsLoaded(true);
        const [topicRes, contentRes] = await Promise.all([
          axios.get(`node/admin/topic/get/${0}`),
          isEdit ? axios.get(`node/admin/content/${libid}`) : null,
        ]);

        if (!isMounted) return;

        if (isEdit && contentRes) {
          if (contentRes.data.resultCode === RESULT_STATUS.SUCCESS) {
            setContentDetails({
              ...contentRes.data.data,
              contentType: contentRes.data.data.videoContent,
              maxtime: contentRes.data.data.timelimit??"",
              description:
                typeof contentRes.data.data.description === "string"
                  ? JSON.parse(contentRes.data.data.description)
                  : contentRes.data.data.description,
            });
          } else {
            notify("Error !", "Unable to get content details");
          }
        }
        if(topicRes.data.resultCode === RESULT_STATUS.SUCCESS)
          setTopicOptions(
            topicRes.data.data.map((item) => ({
              ...item,
              label: item.name,
              value: item.id,
            })) || [],
          );
      } catch (error) {
        if (!isMounted) return;
        handleError(
          error,
          isEdit ? "Unable to get topic or content details" : "Unable to get the topic"
        );
      } finally {
        if (isMounted) setIsLoaded(false);
      }
    };

    getTopicsAndContent();

    return () => {
      isMounted = false;
    };
    // Dependencies are stable (axios, setIsLoaded, setShowNotify, notify, handleError)
  }, [libid, isEdit]);

  const onChange = (field, value) => {
    const validator = validators[field];
    if (validator) {
      const error = validator(value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
    setContentDetails((prev) => ({ ...prev, [field]: value }));
  };

  const onClickBack = () => {
    if (isProceed) {
      setIsProceed(false);
      setIsPreview(false);
    } else if (lpid) {
      navigate(`${baseRoute}/${lpid}/module/${moduleId}?chapterId=${chapterId}`);
    } else {
      navigate(`/library/${type}/questionlist`);
    }
  };

  const onProceedOrPreview = (isPreview = false) => {
    const newErrors = {};
    Object.entries(contentDetails).forEach(([field, value]) => {
      const validator = validators[field];
      if (validator) {
        const error = validator(value) || null;
        if (error) newErrors[field] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsProceed(true);
    setIsPreview(isPreview);
  };

  const onSaveOrUpdate = async (from) => {
    const copyContentDetails = structuredClone(contentDetails);
    copyContentDetails.topic = copyContentDetails.topicid;
    if (typeof copyContentDetails.topicid === "string")
      copyContentDetails.topicname = copyContentDetails.topicid;

    // Parse description blocks
    let descriptionBlocks = [];
    try {
      descriptionBlocks =
        typeof copyContentDetails.description === "string"
          ? JSON.parse(copyContentDetails.description)?.blocks || []
          : copyContentDetails.description?.blocks || [];
    } catch {
      notify("Error !", "Invalid content description format");
      return;
    }

    if (!descriptionBlocks.length) {
      notify("Warning !", "Please add content");
      return;
    }

    // Validate video file names
    for (const block of descriptionBlocks) {
      if (block.data?.url) {
        const fileExtension = block.data.url.split(".").pop().toLowerCase();
        if (fileExtension === "mp4") {
          const fileName = block.data.url.substring(16); // remove "library/content/"
          if (!validateVideoFileName(fileName)) {
            setIsLoaded(false);
            notify(
              "Info",
              "Invalid file name, video filenames contain invalid characters. Allowed Characters(- , _)"
            );
            return;
          }
        }
      }
    }

    try {
      setIsLoaded(true);

      // Prepare file list for upload
      const fileNames = [];
      for (const block of descriptionBlocks) {
        if (["image", "video", "pdf"].includes(block.type) && block.data?.url) {
          fileNames.push(block.data.url.substring(16));
        }
      }

      // Resolve time to pass
      const timeToPass = resolveTimeToPass(
        descriptionBlocks,
        copyContentDetails.maxtime,
        copyContentDetails.contentType
      );

      // Build payload
      let contentData;
      if (isEdit && from === "update") {
        copyContentDetails.id = +libid;
        contentData = JSON.stringify({
          qdata: {
            ...copyContentDetails,
            maxtime: timeToPass,
            description: JSON.stringify(copyContentDetails.description ?? "{}"),
          },
        });
      } else {
        contentData = JSON.stringify({
          qdata: {
            ...copyContentDetails,
            topic: copyContentDetails.topicid,
            description: JSON.stringify(copyContentDetails.description),
            maxtime:
              copyContentDetails.maxtime && copyContentDetails.maxtime > 0
                ? copyContentDetails.maxtime
                : timeToPass,
          },
        });
      }

      const form = new FormData();
      form.append("qdata", contentData);
      form.append("libtypeid", type);
      form.append("libtypename", "content");
      form.append("fileNames", JSON.stringify(fileNames));
      form.append("type", "editor");
      form.append(
        "contentType",
        parseInt(copyContentDetails.contentType) === 0
          ? "content-editor"
          : "video-attachment"
      );

      const { data: libRes } = await axios.post("node/admin/library/add", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (libRes.resultCode !== RESULT_STATUS.SUCCESS) {
        notify("Error !", libRes.message);
        return;
      }

      const questionId = libRes.data?.libid;

      // ==================== FILE UPLOADS WITH Promise.allSettled ====================
      const uploadPromises = [];
      const videoConversionPromises = [];

      for (let i = 0; i < descriptionBlocks.length; i++) {
        const block = descriptionBlocks[i];
        if (!["image", "video", "pdf"].includes(block.type)) continue;
        if (!block.data?.file || !(block.data.file instanceof File)) continue;

        const fname = block.data.url.split("/").pop();
        const filePath = `library/content/${questionId}/${fname}`;

        // Upload file
        uploadPromises.push(
          onMultiPartUpload(filePath, block.data.file)
            .then(() => {
              // If it's an mp4 video, trigger conversion after upload
              const ext = filePath.split(".").pop().toLowerCase();
              if (ext === "mp4") {
                const fileurl = filePath.slice(0, -4);
                const encodedFileUrl = encodeURIComponent(fileurl);
                const convertUrl = `${process.env.REACT_APP_NODE_API}node/admin/video/convert?file=${encodedFileUrl}`;
                videoConversionPromises.push(axios.get(convertUrl));
              }
              // Update block url to final path
              copyContentDetails.description.blocks[i].data.url = filePath;
            })
            .catch((err) => {
              console.error(`Upload failed for ${fname}:`, err);
            })
        );
      }

      // Wait for all uploads to settle (don't fail entire save if one upload fails)
      const uploadResults = await Promise.allSettled(uploadPromises);
      const failedUploads = uploadResults.filter((r) => r.status === "rejected");
      if (failedUploads.length > 0) {
        console.warn(`${failedUploads.length} file(s) failed to upload`);
      }

      // Wait for video conversions (if any)
      if (videoConversionPromises.length > 0) {
        await Promise.allSettled(videoConversionPromises);
      }

      // ==================== ASSIGN TO LEARNING PATH ====================
      if (lpid && from !== "update") {
        const tests = [libRes.data.libid];
        const { data } = await axios.post(
          `node/admin/learningpath/${lpid}/module/${moduleId}/assign-tests`,
          { type: libTypeIds.CONTENT, tests, chapterId }
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          navigate(`${baseRoute}/${lpid}/module/${moduleId}`);
        } else {
          navigate(`/library/${libTypeIds.CONTENT}/testlist${queryString}`);
          throw new Error("The content is created but unable to add to path");
        }
      } else if (lpid && from === "update") {
        navigate(`${baseRoute}/${lpid}/module/${moduleId}`);
      } else {
        navigate(`/library/${libTypeIds.CONTENT}/questionlist`);
        notify("Info", libRes.message);
      }
    } catch (error) {
      handleError(error, `Unable to ${isEdit ? "update" : "create"} the content`);
    } finally {
      setIsLoaded(false);
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="content-entry-container">
      <div className="content-entry-header-wrapper">
        <ReusableBreadcrumbs items={items} />
        <div
          className={`content-entry-header-back-btn-wrapper${
            isPreview ? " content-entry-preview" : ""
          }`}
          onClick={onClickBack}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClickBack()}
          aria-label="Go back"
        >
          <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.7998 10.8L0.799805 5.80005L5.7998 0.800049"
              stroke="#667085"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="content-entry-header-back-btn-text">Back</p>
          {isPreview ? (
            <>
              <svg
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1041_16049)">
                  <path
                    d="M5.20001 1.03584e-05H18.8C19.4833 -0.00135472 20.1601 0.132216 20.7916 0.393056C21.4231 0.653897 21.9969 1.03687 22.48 1.52001C22.9631 2.00315 23.3461 2.57693 23.607 3.20844C23.8678 3.83995 24.0014 4.51675 24 5.20001V18.8C24.0014 19.4833 23.8678 20.1601 23.607 20.7916C23.3461 21.4231 22.9631 21.9969 22.48 22.48C21.9969 22.9631 21.4231 23.3461 20.7916 23.607C20.1601 23.8678 19.4833 24.0014 18.8 24H5.20001C4.51675 24.0014 3.83995 23.8678 3.20844 23.607C2.57693 23.3461 2.00315 22.9631 1.52001 22.48C1.03688 21.9969 0.653898 21.4231 0.393057 20.7916C0.132216 20.1601 -0.00135446 19.4833 1.03544e-05 18.8V5.20001C0.000646344 3.82108 0.548706 2.49881 1.52376 1.52376C2.49881 0.548706 3.82108 0.000646348 5.20001 1.03584e-05Z"
                    fill="url(#paint0_linear_1041_16049)"
                  />
                  <path
                    fill="white"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.3799 5.3999C18.3399 5.3999 19.3599 6.1399 19.9799 6.6399C20.1182 6.74991 20.2296 6.88989 20.3058 7.04926C20.382 7.20863 20.4211 7.38324 20.4199 7.5599V17.2999C20.4146 17.3732 20.3866 17.4431 20.3399 17.4999C20.3144 17.5269 20.2833 17.5481 20.2487 17.5619C20.2142 17.5757 20.1771 17.5818 20.1399 17.5799C20.1057 17.5817 20.0716 17.5754 20.0403 17.5615C20.009 17.5476 19.9815 17.5265 19.9599 17.4999C19.3399 16.9799 18.1199 16.2999 16.3199 16.2999C13.3199 16.2999 12.4399 19.0999 12.4399 19.0999V8.4999C12.4412 7.95591 12.6242 7.42794 12.9599 6.9999C13.5199 6.2599 14.5399 5.3999 16.3799 5.3999ZM7.85992 5.3999C9.69992 5.3999 10.7199 6.2599 11.2999 6.9999C11.6357 7.42794 11.8187 7.95591 11.8199 8.4999V19.0999C11.8199 19.0999 10.9399 16.2999 7.93992 16.2999C6.13992 16.2999 4.91992 16.9799 4.29992 17.4999C4.24393 17.5348 4.18335 17.5617 4.11993 17.5799C4.08291 17.5808 4.04609 17.5742 4.01171 17.5604C3.97733 17.5467 3.94611 17.5261 3.91992 17.4999C3.89374 17.4737 3.87315 17.4425 3.8594 17.4081C3.84565 17.3737 3.83902 17.3369 3.83993 17.2999V7.5599C3.84092 7.3836 3.88088 7.2097 3.95695 7.05065C4.03302 6.8916 4.14331 6.75134 4.27992 6.6399C4.87992 6.1399 5.89992 5.3999 7.85992 5.3999Z"
                  />
                </g>
                <defs>
                  <linearGradient
                    x1="12"
                    x2="12"
                    y2="24"
                    y1="1.03584e-05"
                    id="paint0_linear_1041_16049"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#090953" />
                    <stop offset="1" stopColor="#0A5CB1" />
                  </linearGradient>
                  <clipPath id="clip0_1041_16049">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <h2 className="content-entry-header">{contentDetails.name}</h2>
            </>
          ) : null}
        </div>
        {!isPreview && (
          <h2 className="content-entry-header">
            {isEdit ? "Edit" : "Add"} Content
          </h2>
        )}
      </div>
      {!isProceed ? (
        <div className="content-entry-form">
          <div className="content-entry-form-wrapper">
            <InputDropdown
              options={topicOptions}
              value={contentDetails.topicid}
              placeholder="Select or create topic"
              creatable
              onChange={(value) => onChange("topicid", value)}
              label="Topic"
              hintText={errors.topicid}
              destructive={!!errors.topicid}
            />
            <InputField
              name="name"
              type="text"
              value={contentDetails.name}
              label="Name"
              onChange={(event) =>
                onChange(event.target.name, event.target.value)
              }
              hintText={errors.name}
              destructive={!!errors.name}
            />
            <div className="content-entry-form-type-wrapper">
              <Radio
                size="sm"
                text="Content Editor"
                checked={contentDetails.contentType === 0}
                onChange={(checked) => onChange("contentType", checked ? 0 : 1)}
              />
              <Radio
                size="sm"
                text="Video Attachment"
                checked={contentDetails.contentType === 1}
                onChange={(checked) => onChange("contentType", checked ? 1 : 0)}
              />
            </div>
            <InputField
              name="maxtime"
              type="number"
              value={contentDetails.maxtime}
              label="Max Time(min/s)"
              placeholder="Enter the max time"
              onChange={(event) =>
                onChange(event.target.name, event.target.value)
              }
              hintText={errors.maxtime}
              destructive={!!errors.maxtime}
            />
            <div className="content-entry-form-btn-wrapper">
              <Button
                size="sm"
                hierarchy={{ type: "primary", buttonText: "Proceed" }}
                onClick={() => onProceedOrPreview(false)}
              />
              <Button
                size="sm"
                hierarchy={{ type: "secondaryGrey", buttonText: "Preview" }}
                onClick={() => onProceedOrPreview(true)}
                disabled={!(contentDetails.description?.blocks?.length > 0)}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="content-entry-editor-container">
          <Editor
            userInput={contentDetails}
            setUserInput={setContentDetails}
            qnid={libid}
            readOnly={isPreview}
          />
          {getDataFromStorage("role") !== Roles.tutor && (
            <div className="content-entry-editor-btn-wrapper">
              {!isEdit && (<Button
                size="sm"
                hierarchy={{
                  type: "primary",
                  buttonText: "Save",
                }}
                onClick={() => onSaveOrUpdate("new")}
              />)}
              {isEdit && isAllowedToUpdate ? (
                <Button
                  size="sm"
                  hierarchy={{ type: "primary", buttonText: "Update" }}
                  onClick={() => onSaveOrUpdate("update")}
                />
              ) : null}
            </div>
          )}
        </div>
      )}
      {isLoaded && <Loading />}
    </div>
  );
};

export default ContentEntry;